import { Injectable, inject } from '@angular/core';
import { IFilterService, IPromise, IQService, IWindowService } from 'angular';
import { TODO } from '@b2b/types/core';
import { AutorenewalType, CartMembershipOrderForm, Membership, MembershipItemType } from '@b2b/types/membership';
import { MEMBERSHIP_ENUMS, RULE_CATEGORY_TO_PURCHASE_TYPE } from '@b2b/types/membership/constants';
import { SubscriptionDetail } from '@b2b/types/subscriptions';
import { BehaviorSubject } from 'rxjs';
import { ApiService } from '../../api/api.service';
import { MembershipService } from '../../membership/membership.service';
import { FILTER_PORT, Q_PORT, WINDOW_PORT } from '../../utils';
import { resolveMembershipNameForForm } from './cart-memberships.utils';

/**
 * CartMembershipsService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/cart/memberships/memberships.service.ts
 * Nested sub-service of CartService. Manages membership items, lifecycle eligibility,
 * cardholder data, and subscription state inside the shopping cart.
 *
 * Registered via downgradeInjectable in ng-downgraded-components.ts as 'CartMembershipsService'.
 * Wave 4 — Cart sub-services.
 */

interface CartMembershipsState {
  subscription: {
    status: 'idle' | 'loading' | 'error' | 'success';
    data: SubscriptionDetail | null;
  };
  hasMemberships: boolean;
  hasNewMembership: boolean;
}

function createInitialState(): CartMembershipsState {
  return {
    subscription: { status: 'idle', data: null },
    hasMemberships: false,
    hasNewMembership: false,
  };
}

export type CartMembershipsParent = {
  getMembershipItem: () => any;
  getItems: () => any[];
  getItemByType: (itemType: any) => any;
  setItems: (items: any[], resetCoupons: boolean) => void;
  updateCart: () => Promise<any>;
  updateOrderContact: (membershipItem?: any) => void;
  getOrderContact: () => any;
  updateItem: (eventItem: any) => any;
  addItem: (eventItem: any) => any;
  removeItem: (itemId: any, skipGetCart: boolean) => any;
};

@Injectable({ providedIn: 'root' })
export class CartMembershipsService {
  private $q = inject<IQService>(Q_PORT);
  private $filter = inject<IFilterService>(FILTER_PORT);
  private $window = inject<IWindowService>(WINDOW_PORT);
  private apiService = inject(ApiService);
  private membershipService = inject(MembershipService);

  private stateSubject = new BehaviorSubject<CartMembershipsState>(createInitialState());
  state$ = this.stateSubject.asObservable();

  model: any[] = [];
  parent = {} as CartMembershipsParent;
  membershipLevels: Record<string, any> = {};
  customersDuplicates: any | null = null;
  membershipCartData: any | null = null;
  customerMemberships: TODO[] = [];
  subscriptionData: any | null = null;
  restrictMembershipsToOne = true;
  readonly ENUMS = MEMBERSHIP_ENUMS;

  init(parent: CartMembershipsParent): this {
    this.parent = parent;
    this.initCustomerDuplicates();
    return this;
  }

  initCustomerDuplicates(): void {
    this.customersDuplicates = this.getCustomerDuplicatesFromStorage();
    this.membershipCartData = this.getMembershipCartDataFromStorage();
  }

  getNormalizedAction(action: string): string {
    return action.toLowerCase();
  }

  getSubscriptionData(membership: any): void {
    const currentMembership = membership || null;
    const membershipLevels = currentMembership.level ? [currentMembership.level] : [];

    if (this.hasAutorenewMembershipsInCart(membershipLevels) && !this.subscriptionData) {
      this.stateSubject.next({
        ...this.stateSubject.value,
        subscription: { status: 'loading', data: null },
      });

      this.apiService.subscriptions
        .detail(membership.membershipInfo.membershipId)
        .then((response: any) => {
          this.subscriptionData = response.data.payload;
          this.stateSubject.next({
            ...this.stateSubject.value,
            subscription: { status: 'success', data: response.data.payload },
          });
        })
        .catch(() => {
          this.subscriptionData = null;
          this.stateSubject.next({
            ...this.stateSubject.value,
            subscription: { status: 'error', data: null },
          });
        });
    }
  }

  lifecycle(
    actionTypes: string[],
    membership: { membershipId: number; membershipCategory?: string; membershipName?: string },
  ): Promise<TODO> {
    const q = this.$q.defer<TODO>();
    const eligibilityTypes = actionTypes.join();
    const title = 'Membership is not eligible';

    this.apiService.membership.eligibility
      .listByMembershipCategory({
        membershipId: String(membership.membershipId),
        saleChannels: 'insideSalesIndividual',
        actions: eligibilityTypes,
        summarize: 0,
      })
      .then((response: any) => {
        const eligibility = response.data.payload;
        const eligibleLevels = eligibility.membershipOptions.eligibleMembershipLevels;

        const errorMessage = [
          'The',
          membership.membershipCategory,
          'membership for',
          membership.membershipName,
          'is not eligible to',
          actionTypes.length > 2 ? 'renew' : actionTypes.join('/'),
        ]
          .filter(Boolean)
          .join(' ');

        if (eligibleLevels.length) {
          let _actionAvailable = false;
          eligibleLevels.forEach((eligibleLevel: any) => {
            if (eligibilityTypes.indexOf(eligibleLevel.type) > -1 && eligibleLevel.targetLevels.length) {
              _actionAvailable = true;
            }
          });

          if (_actionAvailable) {
            q.resolve(response);
          } else {
            q.reject({ data: { title, error: errorMessage } });
          }
        } else {
          q.reject({ data: { title, error: errorMessage } });
        }
      })
      .catch((r: any) => q.reject(r));

    return q.promise as Promise<TODO>;
  }

  hasPricepointMatch(
    membership: any,
    eligibility: any,
    type?: any,
  ): { level: any; offering: any; pricepoint: any; type: any } | null {
    const eligibleLevels = eligibility.membershipOptions.eligibleMembershipLevels;
    let result: { level: any; offering: any; pricepoint: any; type: any } | null = null;
    let eligibilityType = type;

    if (!type) {
      eligibleLevels.forEach((eligibleLevel: any) => {
        switch (eligibleLevel.type) {
          case this.ENUMS.renew[0]:
          case this.ENUMS.renew[3]:
          case this.ENUMS.renew[6]:
            eligibilityType = eligibleLevel.type;
            break;
        }
      });
    }

    if (!eligibilityType) {
      eligibilityType = this.ENUMS.renew[0];
    }

    const filteredEligibility = eligibleLevels.filter((el: any) => el.type === eligibilityType);

    if (filteredEligibility.length) {
      filteredEligibility[0].targetLevels.forEach((level: any) => {
        if (level.id === membership.categoryId) {
          level.offerings.forEach((offering: any) => {
            if (
              offering.id === membership.offeringId &&
              offering.priceLists instanceof Array &&
              offering.priceLists.length
            ) {
              offering.priceLists[0].prices.forEach((price: any) => {
                if (price.personType.id === membership.pricePointId) {
                  result = { level, offering, pricepoint: price, type: eligibilityType };
                }
              });
            }
          });
        }
      });
    }

    return result;
  }

  hasPricepointMatchForLevel(membership: any, level: any): boolean {
    let result = false;
    if (level && level.id === membership.categoryId) {
      level.offerings.forEach((offering: any) => {
        if (
          offering.id === membership.offeringId &&
          offering.priceLists instanceof Array &&
          offering.priceLists.length
        ) {
          offering.priceLists[0].prices.forEach((price: any) => {
            if (price.personType.id === membership.pricePointId) result = true;
          });
        }
      });
    }
    return result;
  }

  hasOfferingMatchForLevel(membership: any, level: any): boolean {
    let result = false;
    if (level && level.id === membership.categoryId) {
      level.offerings.forEach((offering: any) => {
        if (
          offering.id === membership.offeringId &&
          offering.priceLists instanceof Array &&
          offering.priceLists.length
        ) {
          result = true;
        }
      });
    }
    return result;
  }

  levelDetail(level: { id: number }): Promise<any> {
    return this.apiService.membership.categories.detail(String(level.id));
  }

  addToCart(membership: any): Promise<any> {
    const q = this.$q.defer();
    const existingItem = this.restrictMembershipsToOne
      ? this.parent.getMembershipItem()
      : this.parent.getItemByType(membership.itemType);
    const membershipItem: any = {
      quantity: 1,
      itemType: membership.itemType,
      membershipInfo: {
        membershipId: membership.membershipId,
        membershipCategoryId: membership.categoryId,
        membershipCategoryName: membership.membershipCategory,
        membershipOfferingId: membership.offeringId,
        membershipOfferingName: membership.offeringName,
        pricePointId: membership.pricePointId,
        pricePointName: membership.pricePointName,
        orderContactId: membership.orderContactId,
        term: membership.term,
        isAutorenew: membership.isAutorenew,
      },
      amount: membership.amount,
      membershipId: membership.membershipId,
    };

    if (membership.membershipId) {
      this.apiService.membership.detail(membership.membershipId).then(
        (response: any) => {
          const m = response.data.payload;
          if (m && m.cardholders) {
            m.cardholders = m.cardholders.sort(this.sortByType);
            m.cardholders = this.processCardholders(m.cardholders);
            membershipItem.membershipInfo.membershipCards = m.cardholders;
            this.parent.updateOrderContact(membershipItem);
          }
          if (existingItem) {
            membershipItem.itemId = existingItem.itemId;
            if (existingItem.membershipInfo?.membershipCards instanceof Array) {
              membershipItem.membershipInfo.membershipCards = existingItem.membershipInfo.membershipCards;
            }
            this.parent.updateItem(membershipItem);
          } else {
            this.parent.addItem(membershipItem);
          }
          this.parent.updateCart().then(
            (r: any) => q.resolve(r),
            (r: any) => q.reject(r),
          );
        },
        (r: any) => q.reject(r),
      );
    } else {
      if (existingItem) {
        membershipItem.itemId = existingItem.itemId;
        if (existingItem.membershipInfo?.membershipCards instanceof Array) {
          membershipItem.membershipInfo.membershipCards = existingItem.membershipInfo.membershipCards;
        }
        this.parent.updateItem(membershipItem);
      } else {
        this.parent.addItem(membershipItem);
      }
      this.parent.updateCart().then(
        (r: any) => q.resolve(r),
        (r: any) => q.reject(r),
      );
    }

    return q.promise as Promise<TODO>;
  }

  updateOrderContact(membership: any): Promise<any> {
    const q = this.$q.defer();
    let membershipItem: any;
    const orderContactId = membership ? membership.orderContactId : null;
    const cartOrderContact = this.parent.getOrderContact();
    const cartOrderContactId = cartOrderContact ? cartOrderContact.customerId : null;
    const updateOrderContactAndCart = (item?: any) => {
      this.parent.updateOrderContact(item);
      this.parent.updateCart().then(
        (r: any) => q.resolve(r),
        (r: any) => q.reject(r),
      );
    };

    if (cartOrderContactId && (!orderContactId || orderContactId !== cartOrderContactId)) {
      this.parent.setItems([], true);
    }

    if (membership) {
      membershipItem = {
        membershipInfo: { membershipCards: membership.cardholders, orderContactId: membership.orderContactId },
      };
      if (membership.cardholders instanceof Array && membership.cardholders.length) {
        updateOrderContactAndCart(membershipItem);
      } else if (membership.membershipId) {
        this.apiService.membership.detail(membership.membershipId).then(
          (response: any) => {
            const m = response.data.payload;
            if (m && m.cardholders) {
              m.cardholders = m.cardholders.sort(this.sortByType);
              membershipItem.membershipInfo.membershipCards = m.cardholders;
            }
            updateOrderContactAndCart(membershipItem);
          },
          (r: any) => q.reject(r),
        );
      }
    } else {
      updateOrderContactAndCart();
    }

    return q.promise as Promise<TODO>;
  }

  sortByType(a: any, b: any): number {
    const aType = a.cardType || '';
    const bType = b.cardType || '';
    if (aType < bType) return -1;
    if (aType > bType) return 1;
    return 0;
  }

  setModel(data: any[]): void {
    const found = data.length ? this.membershipLevels[data[0].membershipInfo.membershipCategoryId] : null;
    if (found && found.itemType === data[0].itemType) {
      data[0].level = found.level;
    }
    this.model = data;
    this.model.forEach((membership) => {
      this.getCardholders(membership).forEach((cardholder: any) => {
        cardholder.membershipId = membership.membershipInfo.membershipId;
        cardholder.address = {
          country: cardholder.country,
          city: cardholder.city,
          streetAddress1: cardholder.streetAddress1,
          streetAddress2: cardholder.streetAddress2,
          state: cardholder.state,
          zipCode: cardholder.zipCode,
        };
      });
    });
    this.updateMembershipState();
  }

  updateMembershipState(): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      hasMemberships: !!this.model.length,
      hasNewMembership: this.hasNewMembership(),
    });
  }

  getModel(): any[] {
    return this.model;
  }

  resetModel(): void {
    this.model = [];
    this.membershipLevels = {};
    this.subscriptionData = null;
    this.clearCustomerData();
    this.updateMembershipState();
  }

  getById(id: string): { index: number; membership: TODO } | false {
    let result: { index: number; membership: TODO } | false = false;
    for (let i = 0, ii = this.model.length; i < ii; i++) {
      if (this.model[i].membershipInfo.membershipCategoryId === id) {
        result = { membership: this.model[i], index: i };
        break;
      }
    }
    return result;
  }

  isMembershipExpired(cardHolder: TODO): boolean {
    let result = false;
    if (cardHolder && cardHolder.membershipStanding) {
      result = cardHolder.membershipStanding !== 'active';
    } else if (cardHolder && cardHolder.cardStatus) {
      result = cardHolder.cardStatus !== 'active';
    }
    return result;
  }

  setOfferings(id: string, levels: any): void {
    const found = this.getById(id);
    if (found) {
      this.model[found.index].offerings = levels;
    }
  }

  setLevel(id: string, level: any): void {
    const found = this.getById(id);
    if (found) {
      const processedLevel = this.processLevel(level);
      this.model[found.index].level = processedLevel;
      this.membershipLevels[level.id] = { level: processedLevel, itemType: this.model[found.index].itemType };
      this.model[found.index].selectedOffering = this.selectOffering(
        this.model[found.index].level.pricepoints,
        this.model[found.index],
      );
    }
  }

  processLevel(level: any): any {
    const result: any[] = [];
    if (this.hasOfferings(level)) {
      level.offerings.forEach((offering: any) => {
        if (
          offering.priceLists instanceof Array &&
          offering.priceLists.length &&
          offering.priceLists[0].prices instanceof Array
        ) {
          offering.priceLists[0].prices.forEach((price: any) => {
            result.push({
              offeringId: offering.id,
              pricepointId: price.personType.id,
              id: this.getPricepointSelectionId(offering.id, price.personType.id),
              displayName: this.getPricepointDisplayName(offering, price.personType.name),
              amount: price.price,
              unitPrice: price.discountedPrice,
              retailUnitPrice: price.price,
              term: offering.term,
              auxGroup: offering.auxGroup,
              actions: offering.actions,
            });
          });
        }
      });
      level.pricepoints = result;
    }
    return level;
  }

  processCardholders(cardholders: any[]): any[] {
    if (cardholders instanceof Array) {
      cardholders.forEach((cardholder: any) => {
        if (cardholder.cardStatus === 'dropped') {
          cardholder.excludeCardOnMemberCreation = true;
        }
      });
    }
    return cardholders;
  }

  selectOffering(pricepoints: any[], membership: any): any {
    let selectedOffering: any;
    pricepoints.forEach((pricepoint: any) => {
      if (
        pricepoint.offeringId === membership.membershipInfo.membershipOfferingId &&
        pricepoint.pricepointId === membership.membershipInfo.pricePointId
      ) {
        selectedOffering = pricepoint;
      }
    });
    if (!selectedOffering && pricepoints.length) {
      selectedOffering = pricepoints[0];
    }
    return selectedOffering;
  }

  hasOfferings(level: any): boolean {
    return level && level.offerings && level.offerings.length;
  }
  getPricepointSelectionId(offeringId: any, pricepointId: any): string {
    return offeringId + '-' + pricepointId;
  }
  getPricepointDisplayName(offering: any, pricePointName: string): string {
    return [offering.name, pricePointName].join(', ');
  }

  getPrimaryCardholder(membership: any): any {
    const result = (this.$filter('filter') as any)(this.getCardholders(membership), (c: any) =>
      this.isPrimaryCardholder(c),
    );
    return result.length ? result[0] : null;
  }

  isPrimaryCardholder(cardholder: any): boolean {
    return this.isCardType(cardholder, 'primary');
  }
  isSecondaryCardholder(cardholder: any): boolean {
    return this.isCardType(cardholder, 'secondary');
  }
  isCardType(cardholder: any, cardType: string): boolean {
    return cardholder.cardType === cardType;
  }

  isCardholderACustomer(cardholder: any, customerId: any): boolean {
    let result = cardholder && !!cardholder.customerId;
    // eslint-disable-next-line eqeqeq
    if (result && customerId) {
      result = result && cardholder.customerId == customerId;
    }
    return result;
  }

  getOrderContact(cardholders: any[], customerId: any): any | null {
    let result: any | null = null;
    if (cardholders instanceof Array) {
      result = this.getCustomer(cardholders, customerId);
      if (!result) {
        const primaryCardholders = (this.$filter('filter') as any)(cardholders, (c: any) =>
          this.isPrimaryCardholder(c),
        );
        if (primaryCardholders) {
          this.getCustomer(primaryCardholders, customerId);
        } else {
          const secondaryCardholders = (this.$filter('filter') as any)(cardholders, (c: any) =>
            this.isSecondaryCardholder(c),
          );
          this.getCustomer(secondaryCardholders, customerId);
        }
      }
    }
    return result;
  }

  getCustomer(cardholders: any[], customerId: any): any | null {
    let result: any | null = null;
    cardholders.forEach((cardholder) => {
      if (!result && this.isCardholderACustomer(cardholder, customerId)) {
        result = cardholder;
      }
    });
    return result;
  }

  getCardholder(membership: any, id: any): any {
    return (this.$filter('filter') as any)(this.getCardholders(membership), (c: any) => c.id === id)[0];
  }

  getCardholders(membership: any): any[] {
    if (membership?.membershipInfo?.membershipCards) {
      return membership.membershipInfo.membershipCards;
    }
    return [];
  }

  getDiscountMembershipLevelIds(_newOrRejoinOnly?: boolean): any[] {
    const membership = this.parent.getMembershipItem();
    const membershipLevelIds: any[] = [];
    if (membership) {
      const action = this.membershipService.getActionFromPurchaseType(membership.itemType);
      switch (action) {
        case this.ENUMS.renew[3]:
        case this.ENUMS.renew[4]:
        case this.ENUMS.renew[5]:
        case this.ENUMS.renew[6]:
        case this.ENUMS.renew[7]:
        case this.ENUMS.renew[8]:
        case this.ENUMS.eligibilityTypes[0]:
          membershipLevelIds.push(membership.membershipInfo.membershipCategoryId);
          break;
      }
    }
    return membershipLevelIds;
  }

  hasNewMembership(): any {
    return this.parent.getItemByType(this.membershipService.purchaseTypes['new_memberships'].name);
  }

  getUpgradeDowngradeMembership(): any[] {
    // const eligibilityTypes = this.ENUMS.upgradeDowngrade.concat(this.ENUMS.upgradeDowngradeCart);
    const eligibilityTypes = [...this.ENUMS.upgradeDowngrade, ...this.ENUMS.upgradeDowngradeCart];
    return this.parent.getItems().filter((item) => eligibilityTypes.includes(item.itemType));
  }

  hasMemberships(): boolean {
    // const eligibilityTypes = this.ENUMS.eligibilityTypes.concat(this.ENUMS.eligibilityTypesCart);
    const eligibilityTypes = [...this.ENUMS.eligibilityTypes, ...this.ENUMS.eligibilityTypesCart];
    return this.parent.getItems().some((item) => eligibilityTypes.includes(item.itemType));
  }

  isNewMembership(type: string): boolean {
    return type === this.membershipService.purchaseTypes['new_memberships'].name;
  }

  setMembershipCartData(membershipCartData: any): void {
    this.$window.sessionStorage.setItem('membershipCartData', JSON.stringify(membershipCartData));
    this.membershipCartData = membershipCartData;
  }

  getMembershipCartData(): any {
    return this.membershipCartData;
  }

  getMembershipCartDataFromStorage(): any | null {
    const json = this.$window.sessionStorage.getItem('membershipCartData');
    return json ? JSON.parse(json) : null;
  }

  clearCustomerMemberships(): void {
    this.customerMemberships = [];
  }
  setCustomerMemberships(customerMemberships: any[]): void {
    this.customerMemberships = customerMemberships;
  }
  getCustomerMemberships(): TODO[] {
    return this.customerMemberships;
  }

  setCustomerDuplicates(customersDuplicates: any): void {
    this.$window.sessionStorage.setItem('customersDuplicates', JSON.stringify(customersDuplicates));
    this.customersDuplicates = customersDuplicates;
  }

  getCustomerDuplicates(): any {
    return this.customersDuplicates;
  }

  getCustomerDuplicatesFromStorage(): any | null {
    const json = this.$window.sessionStorage.getItem('customersDuplicates');
    return json ? JSON.parse(json) : null;
  }

  getDupesStatus(customerDuplicate: {
    possibleDuplicates?: TODO[] | null;
    dupesStatus: TODO;
  }): 'unknown' | 'potential_match' | 'selected_existing_customer' | 'none_match' {
    const hasPossibleDuplicates =
      customerDuplicate.possibleDuplicates && customerDuplicate.possibleDuplicates.length > 0;
    const isUnknownStatus = customerDuplicate.dupesStatus == null || customerDuplicate.dupesStatus === 'unknown';
    if (!hasPossibleDuplicates) return 'unknown';
    if (hasPossibleDuplicates && isUnknownStatus) return 'potential_match';
    return customerDuplicate.dupesStatus;
  }

  clearCustomerData(): void {
    if (this.$window.sessionStorage.getItem('customersDuplicates')) {
      this.$window.sessionStorage.removeItem('customersDuplicates');
      this.customersDuplicates = null;
    }
    if (this.$window.sessionStorage.getItem('membershipCartData')) {
      this.$window.sessionStorage.removeItem('membershipCartData');
      this.membershipCartData = null;
    }
  }

  getCardholderInfo(): any {
    const currentMembership = this.membershipCartData;
    if (!currentMembership) return undefined;
    const primaryCardholder = this.getPrimaryCardholderInfo(currentMembership);
    const cardHolder = JSON.parse(JSON.stringify(primaryCardholder));
    cardHolder.membershipLevel = currentMembership.level.name;
    cardHolder.nameOnCard = currentMembership.membershipInfo.membershipCards[0].name;
    return JSON.parse(JSON.stringify({ cardType: primaryCardholder.cardType, cardHolder }));
  }

  getPrimaryCardholderInfo(membership: any): any {
    let memberships: any[] = [];
    if (membership?.membershipInfo?.membershipCards instanceof Array) {
      memberships = membership.membershipInfo.membershipCards.filter((c: any) => c.cardType === 'primary');
    }
    return memberships.length ? memberships[0] : null;
  }

  findSelectedOfferingFromMembershipLevels(membershipLevels: object[], membership: any): any {
    const membershipLevel = this.extractMembershipLevel(membershipLevels, membership);
    const offerings = (membershipLevel && (membershipLevel as any).offerings) || [];
    return offerings.find((offering: any) => offering.id === membership.membershipInfo.membershipOfferingId);
  }

  extractMembershipLevel(membershipLevels: any[], membership: any): any {
    const actionType = this.membershipService.getActionFromPurchaseType(membership.itemType);
    const membershipCategoryId = membership.membershipInfo.membershipCategoryId;
    for (const membershipLevel of membershipLevels) {
      if (membershipLevel.membershipOptions) {
        for (const el of membershipLevel.membershipOptions.eligibleMembershipLevels) {
          if (el.type !== actionType) continue;
          for (const targetLevel of el.targetLevels) {
            if (targetLevel.id === membershipCategoryId) return targetLevel;
          }
        }
      } else if (membershipLevel.id === membershipCategoryId) {
        return membershipLevel;
      }
    }
    return undefined;
  }

  isAutorenewType(params: { autorenewalType: AutorenewalType | undefined }): boolean {
    return params.autorenewalType === 'required' || params.autorenewalType === 'optional';
  }

  isAutorenewMembership(membershipLevels: object[], membership: Pick<Membership, 'membershipInfo'>): boolean {
    const selectedOffering = this.findSelectedOfferingFromMembershipLevels(membershipLevels, membership);
    const isAutorenew = this.isAutorenewType({ autorenewalType: selectedOffering?.autorenewalType });
    const enabled = (membership.membershipInfo as any).isAutorenew === true;
    return isAutorenew && enabled;
  }

  hasAutorenewMemberships(membershipLevels: object[]): boolean {
    return this.model.some((membership) => this.isAutorenewMembership(membershipLevels, membership));
  }

  hasAutorenewMembershipsInCart(membershipLevels: object[]): boolean {
    return this.model.some((membership) => {
      const selectedOffering = this.findSelectedOfferingFromMembershipLevels(membershipLevels, membership);
      return this.isAutorenewType({ autorenewalType: selectedOffering ? selectedOffering.autorenewalType : null });
    });
  }

  fetchMembershipLevelDetail(membership: {
    itemType: TODO;
    membershipInfo: TODO;
    membershipId: number;
    membershipCategory: string;
    membershipName: string;
  }): Promise<TODO | undefined> {
    const actionType = this.membershipService.getActionFromPurchaseType(membership.itemType);
    if (membership.membershipInfo.membershipId && actionType) {
      return this.lifecycle([actionType], membership).then((response: any) => response.data.payload);
    }
    if (membership.membershipInfo.membershipCategoryId) {
      return this.levelDetail({ id: membership.membershipInfo.membershipCategoryId }).then(
        (response: any) => response.data.payload,
      );
    }
    return Promise.resolve(undefined);
  }

  send(event: { type: 'RESET' }): void {
    switch (event.type) {
      case 'RESET':
        this.stateSubject.next(createInitialState());
        break;
      default:
        break;
    }
  }

  getForms(): Promise<CartMembershipOrderForm[]> {
    const membershipLevelIds = this.model.map((item) => item.membershipInfo.membershipCategoryId).join(',');
    if (!membershipLevelIds) {
      return Promise.resolve([]);
    }
    const membershipOfferingIds = this.model.map((item) => item.membershipInfo.membershipOfferingId).join(',');
    const lifecycleActions = this.model
      .map((item) => {
        const ruleCategories =
          this.ENUMS.MembershipPurchaseTypesToRuleCategoryMap[
            item.itemType as keyof typeof this.ENUMS.MembershipPurchaseTypesToRuleCategoryMap
          ];
        return ruleCategories ? ruleCategories[0] : null;
      })
      .filter((action) => action !== null)
      .join(',');

    return this.apiService.membership.lifecycle.forms
      .list({ lifecycleActions, membershipLevelIds, membershipOfferingIds })
      .then((response: any) => {
        if (!response.data.success) {
          return [];
        }
        return response.data.payload.list.map((lifecycleForm: any) => ({
          uuid: `${lifecycleForm.form.id}-${lifecycleForm.id}-${lifecycleForm.lifecycleAction}`,
          ...lifecycleForm.form,
          membershipName: resolveMembershipNameForForm(lifecycleForm, this.model),
          itemType: (RULE_CATEGORY_TO_PURCHASE_TYPE[
            lifecycleForm.lifecycleAction as keyof typeof RULE_CATEGORY_TO_PURCHASE_TYPE
          ] ?? lifecycleForm.lifecycleAction) as MembershipItemType,
          membershipOfferingId: this.model.find((item) =>
            lifecycleForm.membershipLevelsInfo?.some(
              (levelInfo: any) => levelInfo?.membershipLevel?.id === item.membershipInfo?.membershipCategoryId,
            ),
          )?.membershipInfo?.membershipOfferingId,
          formPreferences: lifecycleForm.formPreferences,
          membershipLevelsInfo: lifecycleForm.membershipLevelsInfo,
          displayFormForGiftPurchase: lifecycleForm.displayFormForGiftPurchase,
          displayFormForStandardPurchase: lifecycleForm.displayFormForStandardPurchase,
          displayFormForAutoRenewPurchase: lifecycleForm.displayFormForAutoRenewPurchase,
        }));
      });
  }
}